import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppBranding from './AppBranding';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Slider } from './ui/slider';
import { 
  ArrowLeft, 
  Settings, 
  Key, 
  TrendingUp,
  Shield,
  Bell,
  Save,
  RefreshCw,
  Check,
  X,
  AlertTriangle,
  Eye,
  EyeOff,
  Trash2,
  TestTube,
  Zap,
  Grid3X3,
  Activity,
  Wallet,
  DollarSign,
  Building2,
  Plus,
  Link,
  Unlink,
  Clock,
  GitBranch,
  Send,
  MessageSquare,
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import CoinOverviewTable from './CoinOverviewTable';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V2 = `${BACKEND_URL}/api/v2`;

// Exchange logos/colors
const EXCHANGE_CONFIG = {
  coinbase: { color: '#0052FF', name: 'Coinbase' },
  binance: { color: '#F3BA2F', name: 'Binance' },
  kraken: { color: '#5741D9', name: 'Kraken' },
  crypto_com: { color: '#002D74', name: 'Crypto.com' }
};

const UserConfigPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [config, setConfig] = useState(null);
  
  // Exchange state
  const [availableExchanges, setAvailableExchanges] = useState([]);
  const [userExchanges, setUserExchanges] = useState([]);
  const [showExchangeForm, setShowExchangeForm] = useState(false);
  const [selectedExchange, setSelectedExchange] = useState('');
  const [exchangeApiKey, setExchangeApiKey] = useState('');
  const [exchangeApiSecret, setExchangeApiSecret] = useState('');
  const [showExchangeSecret, setShowExchangeSecret] = useState(false);
  const [testingExchange, setTestingExchange] = useState(false);
  const [useUsEndpoint, setUseUsEndpoint] = useState(false);


  // Fetch user config and strategies
  const fetchConfig = useCallback(async () => {
    try {
      const [configRes, strategiesRes] = await Promise.all([
        axios.get(`${API_V2}/user/config`, { withCredentials: true }),
        axios.get(`${API_V2}/user/config/strategies`, { withCredentials: true })
      ]);
      setConfig(configRes.data);
      setStrategies(strategiesRes.data.strategies || []);
    } catch (error) {
      console.error('Failed to fetch config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch exchanges
  const fetchExchanges = useCallback(async () => {
    try {
      const [availableRes, userRes] = await Promise.all([
        axios.get(`${API_V2}/exchanges/available`, { withCredentials: true }),
        axios.get(`${API_V2}/exchanges/user`, { withCredentials: true })
      ]);
      setAvailableExchanges(availableRes.data.exchanges || []);
      setUserExchanges(userRes.data.exchanges || []);
    } catch (error) {
      console.error('Failed to fetch exchanges:', error);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchExchanges();
  }, [fetchConfig, fetchExchanges]);

  // Test exchange connection
  const handleTestExchange = async () => {
    if (!selectedExchange || !exchangeApiKey || !exchangeApiSecret) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setTestingExchange(true);
    try {
      const response = await axios.post(`${API_V2}/exchanges/test`, {
        exchange: selectedExchange,
        api_key: exchangeApiKey,
        api_secret: exchangeApiSecret,
        use_us_endpoint: useUsEndpoint
      }, { withCredentials: true });
      
      if (response.data.success) {
        toast.success(`Connected to ${EXCHANGE_CONFIG[selectedExchange]?.name || selectedExchange}!`);
      } else {
        toast.error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingExchange(false);
    }
  };

  // Configure exchange
  const handleConfigureExchange = async (isPrimary = false) => {
    if (!selectedExchange || !exchangeApiKey || !exchangeApiSecret) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_V2}/exchanges/configure`, {
        exchange: selectedExchange,
        api_key: exchangeApiKey,
        api_secret: exchangeApiSecret,
        is_primary: isPrimary,
        use_us_endpoint: useUsEndpoint
      }, { withCredentials: true });
      
      toast.success(`${EXCHANGE_CONFIG[selectedExchange]?.name || selectedExchange} configured successfully!`);
      setShowExchangeForm(false);
      setSelectedExchange('');
      setExchangeApiKey('');
      setExchangeApiSecret('');
      fetchExchanges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to configure exchange');
    } finally {
      setSaving(false);
    }
  };

  // Remove exchange
  const handleRemoveExchange = async (exchangeId) => {
    if (!window.confirm(`Remove ${EXCHANGE_CONFIG[exchangeId]?.name || exchangeId}?`)) return;
    
    try {
      await axios.delete(`${API_V2}/exchanges/${exchangeId}`, { withCredentials: true });
      toast.success('Exchange removed');
      fetchExchanges();
    } catch (error) {
      toast.error('Failed to remove exchange');
    }
  };

  // Save configuration
  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_V2}/user/config`, config, { withCredentials: true });
      toast.success('Configuration saved successfully');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Update config field
  const updateConfig = (path, value) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      const keys = path.split('.');
      let obj = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  // Get strategy icon
  const getStrategyIcon = (strategyId) => {
    switch (strategyId) {
      case 'rsi': return <Activity className="h-5 w-5" />;
      case 'grid': return <Grid3X3 className="h-5 w-5" />;
      case 'macd': return <TrendingUp className="h-5 w-5" />;
      case 'dca': return <Clock className="h-5 w-5" />;
      case 'ema': return <GitBranch className="h-5 w-5" />;
      default: return <Zap className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#00FF41] text-xl tracking-widest">
          LOADING CONFIGURATION...
        </div>
      </div>
    );
  }

  const selectedStrategy = strategies.find(s => s.id === config?.strategy) || strategies[0];

  return (
    <div className="min-h-screen">
      <AppBranding />
      <div className="p-4">
      <Toaster position="top-right" theme="dark" />
      
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              className="matrix-btn rounded-none text-xs h-8"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              BACK TO DASHBOARD
            </Button>
            <div className="flex items-center gap-2">
              <Settings className="h-6 w-6 text-[#00FF41]" />
              <h1 className="text-2xl font-bold text-[#00FF41] tracking-widest">
                MY CONFIGURATION
              </h1>
            </div>
          </div>
          <Button
            onClick={handleSaveConfig}
            disabled={saving}
            className="matrix-btn rounded-none h-10 border-[#00FF41] text-[#00FF41] hover:bg-[#00FF41] hover:text-black"
            data-testid="save-config-btn"
          >
            {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            SAVE ALL CHANGES
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <Tabs defaultValue="strategy" className="w-full">
        <TabsList className="grid w-full grid-cols-5 bg-black border border-[#003B00] rounded-none h-12">
          <TabsTrigger value="strategy" className="data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] rounded-none text-xs">
            <Zap className="h-4 w-4 mr-1" />
            STRATEGY
          </TabsTrigger>
          <TabsTrigger value="exchanges" className="data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] rounded-none text-xs" data-testid="exchanges-tab">
            <Building2 className="h-4 w-4 mr-1" />
            EXCHANGES
          </TabsTrigger>
          <TabsTrigger value="trading" className="data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] rounded-none text-xs">
            <TrendingUp className="h-4 w-4 mr-1" />
            TRADING
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] rounded-none text-xs">
            <Bell className="h-4 w-4 mr-1" />
            ALERTS
          </TabsTrigger>
        </TabsList>

        {/* Strategy Tab */}
        <TabsContent value="strategy" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Strategy Selection */}
            <Card className="matrix-card rounded-none border border-[#003B00] lg:col-span-2">
              <CardHeader className="border-b border-[#003B00] pb-3">
                <CardTitle className="text-[#00FF41] text-sm tracking-widest">
                  SELECT TRADING STRATEGY
                </CardTitle>
                <CardDescription className="text-[#008F11] text-xs">
                  Choose the strategy that best fits your trading style
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {strategies.map(strategy => (
                    <div
                      key={strategy.id}
                      onClick={() => updateConfig('strategy', strategy.id)}
                      className={`p-4 border cursor-pointer transition-all ${
                        config?.strategy === strategy.id
                          ? 'border-[#00FF41] bg-[#003B00]/30'
                          : 'border-[#003B00] hover:border-[#008F11]'
                      }`}
                      data-testid={`strategy-${strategy.id}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={config?.strategy === strategy.id ? 'text-[#00FF41]' : 'text-[#008F11]'}>
                          {getStrategyIcon(strategy.id)}
                        </div>
                        <span className={`font-bold text-sm ${config?.strategy === strategy.id ? 'text-[#00FF41]' : 'text-[#008F11]'}`}>
                          {strategy.name}
                        </span>
                      </div>
                      <p className="text-[#008F11] text-xs mb-3">{strategy.description}</p>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className={`text-xs ${
                          strategy.risk_level === 'Low-Medium' ? 'border-green-500 text-green-500' :
                          strategy.risk_level === 'Medium' ? 'border-yellow-500 text-yellow-500' :
                          'border-orange-500 text-orange-500'
                        }`}>
                          {strategy.risk_level}
                        </Badge>
                        {config?.strategy === strategy.id && (
                          <Check className="h-4 w-4 text-[#00FF41]" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Strategy Parameters */}
            <Card className="matrix-card rounded-none border border-[#003B00]">
              <CardHeader className="border-b border-[#003B00] pb-3">
                <CardTitle className="text-[#00FF41] text-sm tracking-widest flex items-center gap-2">
                  {getStrategyIcon(config?.strategy)}
                  {selectedStrategy?.name?.toUpperCase()} PARAMETERS
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <ScrollArea className="h-[300px]">
                  <div className="space-y-4">
                    {selectedStrategy?.params?.map(param => (
                      <div key={param.name} className="space-y-2">
                        {param.type === 'boolean' ? (
                          <div className="flex items-center justify-between">
                            <Label className="text-[#008F11] text-xs">{param.label || param.name.toUpperCase()}</Label>
                            <button
                              onClick={() => {
                                const current = config?.strategy_params?.[config?.strategy]?.[param.name] ?? param.default;
                                updateConfig(`strategy_params.${config?.strategy}.${param.name}`, !current);
                              }}
                              className={`px-3 py-1 text-xs border ${
                                (config?.strategy_params?.[config?.strategy]?.[param.name] ?? param.default)
                                  ? 'bg-[#003B00] text-[#00FF41] border-[#00FF41]'
                                  : 'bg-black text-gray-500 border-[#003B00]'
                              }`}
                              data-testid={`param-toggle-${param.name}`}
                            >
                              {(config?.strategy_params?.[config?.strategy]?.[param.name] ?? param.default) ? 'ON' : 'OFF'}
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between">
                              <Label className="text-[#008F11] text-xs">{param.label || param.name.toUpperCase()}</Label>
                              <span className="text-[#00FF41] text-sm font-mono">
                                {config?.strategy_params?.[config?.strategy]?.[param.name] ?? param.default}
                              </span>
                            </div>
                            <Slider
                              value={[config?.strategy_params?.[config?.strategy]?.[param.name] ?? param.default]}
                              min={param.min}
                              max={param.max}
                              step={param.step || (param.type === 'number' && param.max <= 100 ? 1 : 0.1)}
                              onValueChange={([value]) => updateConfig(`strategy_params.${config?.strategy}.${param.name}`, value)}
                              className="w-full"
                            />
                          </>
                        )}
                        {param.description && <p className="text-[#003B00] text-[10px]">{param.description}</p>}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Exchanges Tab */}
        <TabsContent value="exchanges" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Connected Exchanges */}
            <Card className="matrix-card rounded-none border border-[#003B00]">
              <CardHeader className="border-b border-[#003B00] pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-[#00FF41] text-sm tracking-widest">
                      CONNECTED EXCHANGES
                    </CardTitle>
                    <CardDescription className="text-[#008F11] text-xs">
                      Your configured trading exchanges
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => setShowExchangeForm(true)}
                    className="matrix-btn rounded-none text-xs h-8"
                    data-testid="add-exchange-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    ADD EXCHANGE
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {userExchanges.length === 0 ? (
                  <div className="text-center py-8">
                    <Building2 className="h-12 w-12 mx-auto text-[#003B00] mb-3" />
                    <p className="text-[#008F11] text-sm">No exchanges connected</p>
                    <p className="text-[#006400] text-xs mt-1">Add an exchange to start trading</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {userExchanges.map((exchange) => (
                      <div
                        key={exchange.exchange}
                        className="flex items-center justify-between p-3 border border-[#003B00] bg-[#001500]"
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded flex items-center justify-center text-white font-bold text-sm"
                            style={{ backgroundColor: EXCHANGE_CONFIG[exchange.exchange]?.color || '#333' }}
                          >
                            {(EXCHANGE_CONFIG[exchange.exchange]?.name || exchange.exchange).charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-[#00FF41] font-bold text-sm flex items-center gap-2">
                              {EXCHANGE_CONFIG[exchange.exchange]?.name || exchange.exchange}
                              {exchange.is_primary && (
                                <Badge className="bg-yellow-900 text-yellow-200 border-yellow-600 text-[10px]">
                                  PRIMARY
                                </Badge>
                              )}
                            </div>
                            <div className="text-[#008F11] text-xs">
                              {exchange.api_key_masked || '****'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${exchange.connected ? 'bg-green-900 text-green-200 border-green-600' : 'bg-red-900 text-red-200 border-red-600'} text-[10px]`}>
                            {exchange.connected ? (
                              <><Link className="h-3 w-3 mr-1" /> CONNECTED</>
                            ) : (
                              <><Unlink className="h-3 w-3 mr-1" /> DISCONNECTED</>
                            )}
                          </Badge>
                          <Button
                            onClick={() => handleRemoveExchange(exchange.exchange)}
                            className="matrix-btn rounded-none text-xs h-7 border-red-500 text-red-500 hover:bg-red-900/30"
                            data-testid={`remove-${exchange.exchange}-btn`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add Exchange Form / Available Exchanges */}
            <Card className="matrix-card rounded-none border border-[#003B00]">
              <CardHeader className="border-b border-[#003B00] pb-3">
                <CardTitle className="text-[#00FF41] text-sm tracking-widest">
                  {showExchangeForm ? 'ADD NEW EXCHANGE' : 'AVAILABLE EXCHANGES'}
                </CardTitle>
                <CardDescription className="text-[#008F11] text-xs">
                  {showExchangeForm ? 'Enter your API credentials' : 'Supported trading platforms'}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4">
                {showExchangeForm ? (
                  <div className="space-y-4">
                    {/* Exchange Selector */}
                    <div>
                      <Label className="text-[#00FF41] text-xs">SELECT EXCHANGE</Label>
                      <select
                        value={selectedExchange}
                        onChange={(e) => setSelectedExchange(e.target.value)}
                        className="w-full mt-1 h-10 px-3 bg-black border border-[#003B00] text-[#00FF41] rounded-none focus:border-[#00FF41]"
                        data-testid="exchange-select"
                      >
                        <option value="">-- Select Exchange --</option>
                        {availableExchanges.map((ex) => (
                          <option key={ex.id} value={ex.id}>
                            {ex.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* API Key */}
                    <div>
                      <Label className="text-[#00FF41] text-xs">API KEY</Label>
                      <Input
                        type="text"
                        value={exchangeApiKey}
                        onChange={(e) => setExchangeApiKey(e.target.value)}
                        placeholder="Enter API key"
                        className="matrix-input rounded-none mt-1"
                        data-testid="exchange-api-key"
                      />
                    </div>

                    {/* API Secret */}
                    <div>
                      <Label className="text-[#00FF41] text-xs">API SECRET</Label>
                      <div className="relative">
                        <Input
                          type={showExchangeSecret ? "text" : "password"}
                          value={exchangeApiSecret}
                          onChange={(e) => setExchangeApiSecret(e.target.value)}
                          placeholder="Enter API secret"
                          className="matrix-input rounded-none mt-1 pr-10"
                          data-testid="exchange-api-secret"
                        />
                        <button
                          type="button"
                          onClick={() => setShowExchangeSecret(!showExchangeSecret)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#008F11] hover:text-[#00FF41]"
                        >
                          {showExchangeSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Binance US option */}
                    {selectedExchange === 'binance' && (
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={useUsEndpoint}
                          onCheckedChange={setUseUsEndpoint}
                        />
                        <Label className="text-[#008F11] text-xs">Use Binance US endpoint</Label>
                      </div>
                    )}

                    {/* Buttons */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={handleTestExchange}
                        disabled={testingExchange}
                        className="matrix-btn rounded-none flex-1 text-xs"
                        data-testid="test-exchange-btn"
                      >
                        {testingExchange ? (
                          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <TestTube className="h-3 w-3 mr-1" />
                        )}
                        TEST CONNECTION
                      </Button>
                      <Button
                        onClick={() => handleConfigureExchange(userExchanges.length === 0)}
                        disabled={saving}
                        className="matrix-btn rounded-none flex-1 text-xs border-[#00FF41] text-[#00FF41] hover:bg-[#00FF41] hover:text-black"
                        data-testid="save-exchange-btn"
                      >
                        <Save className="h-3 w-3 mr-1" />
                        SAVE
                      </Button>
                    </div>
                    <Button
                      onClick={() => {
                        setShowExchangeForm(false);
                        setSelectedExchange('');
                        setExchangeApiKey('');
                        setExchangeApiSecret('');
                      }}
                      className="w-full matrix-btn rounded-none text-xs border-[#003B00] text-[#008F11]"
                    >
                      CANCEL
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availableExchanges.map((exchange) => {
                      const isConnected = userExchanges.some(ue => ue.exchange === exchange.id);
                      return (
                        <div
                          key={exchange.id}
                          className={`p-3 border ${isConnected ? 'border-[#00FF41] bg-[#001a00]' : 'border-[#003B00]'} hover:border-[#008F11] transition-colors`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded flex items-center justify-center text-white font-bold"
                                style={{ backgroundColor: EXCHANGE_CONFIG[exchange.id]?.color || '#333' }}
                              >
                                {exchange.name.charAt(0)}
                              </div>
                              <div>
                                <div className="text-[#00FF41] font-bold text-sm">{exchange.name}</div>
                                <div className="text-[#006400] text-xs">{exchange.description}</div>
                              </div>
                            </div>
                            {isConnected ? (
                              <Badge className="bg-green-900 text-green-200 border-green-600 text-[10px]">
                                <Check className="h-3 w-3 mr-1" /> CONNECTED
                              </Badge>
                            ) : (
                              <Button
                                onClick={() => {
                                  setSelectedExchange(exchange.id);
                                  setShowExchangeForm(true);
                                }}
                                className="matrix-btn rounded-none text-xs h-7"
                              >
                                CONNECT
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Trading Tab */}
        <TabsContent value="trading" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Trading Parameters */}
            <Card className="matrix-card rounded-none border border-[#003B00]">
              <CardHeader className="border-b border-[#003B00] pb-3">
                <CardTitle className="text-[#00FF41] text-sm tracking-widest">
                  TRADING PARAMETERS
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#008F11] text-xs">TRADE AMOUNT (USD)</Label>
                  <Input
                    type="number"
                    value={config?.trading_params?.trade_amount_usd ?? 20}
                    onChange={(e) => updateConfig('trading_params.trade_amount_usd', parseFloat(e.target.value))}
                    className="matrix-input rounded-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#008F11] text-xs">TRADE PERCENT (%)</Label>
                  <Input
                    type="number"
                    value={config?.trading_params?.trade_percent ?? 10}
                    onChange={(e) => updateConfig('trading_params.trade_percent', parseFloat(e.target.value))}
                    className="matrix-input rounded-none"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-[#008F11] text-xs">USE PERCENTAGE TRADING</Label>
                  <Switch
                    checked={config?.trading_params?.use_percent ?? true}
                    onCheckedChange={(checked) => updateConfig('trading_params.use_percent', checked)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#008F11] text-xs">MIN TRADE (USD)</Label>
                  <Input
                    type="number"
                    value={config?.trading_params?.min_trade_usd ?? 20}
                    onChange={(e) => updateConfig('trading_params.min_trade_usd', parseFloat(e.target.value))}
                    className="matrix-input rounded-none"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Protection Settings */}
            <Card className="matrix-card rounded-none border border-[#003B00]">
              <CardHeader className="border-b border-[#003B00] pb-3">
                <CardTitle className="text-[#00FF41] text-sm tracking-widest flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  PROTECTION SETTINGS
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-[#008F11] text-xs">STOP LOSS</Label>
                  <Switch
                    checked={config?.protection_params?.stop_loss_enabled ?? true}
                    onCheckedChange={(checked) => updateConfig('protection_params.stop_loss_enabled', checked)}
                  />
                </div>
                {config?.protection_params?.stop_loss_enabled && (
                  <div className="space-y-2 pl-4 border-l border-[#003B00]">
                    <Label className="text-[#008F11] text-xs">STOP LOSS % : {config?.protection_params?.stop_loss_percent ?? 5}%</Label>
                    <Slider
                      value={[config?.protection_params?.stop_loss_percent ?? 5]}
                      min={1}
                      max={20}
                      step={0.5}
                      onValueChange={([value]) => updateConfig('protection_params.stop_loss_percent', value)}
                    />
                  </div>
                )}
                
                <Separator className="bg-[#003B00]" />
                
                <div className="flex items-center justify-between">
                  <Label className="text-[#008F11] text-xs">TAKE PROFIT</Label>
                  <Switch
                    checked={config?.protection_params?.take_profit_enabled ?? true}
                    onCheckedChange={(checked) => updateConfig('protection_params.take_profit_enabled', checked)}
                  />
                </div>
                {config?.protection_params?.take_profit_enabled && (
                  <div className="space-y-2 pl-4 border-l border-[#003B00]">
                    <Label className="text-[#008F11] text-xs">TAKE PROFIT % : {config?.protection_params?.take_profit_percent ?? 10}%</Label>
                    <Slider
                      value={[config?.protection_params?.take_profit_percent ?? 10]}
                      min={1}
                      max={50}
                      step={1}
                      onValueChange={([value]) => updateConfig('protection_params.take_profit_percent', value)}
                    />
                  </div>
                )}
                
                <Separator className="bg-[#003B00]" />
                
                <div className="flex items-center justify-between">
                  <Label className="text-[#008F11] text-xs">TRAILING STOP</Label>
                  <Switch
                    checked={config?.protection_params?.trailing_stop_enabled ?? false}
                    onCheckedChange={(checked) => updateConfig('protection_params.trailing_stop_enabled', checked)}
                  />
                </div>
                {config?.protection_params?.trailing_stop_enabled && (
                  <div className="space-y-2 pl-4 border-l border-[#003B00]">
                    <Label className="text-[#008F11] text-xs">TRAILING % : {config?.protection_params?.trailing_stop_percent ?? 3}%</Label>
                    <Slider
                      value={[config?.protection_params?.trailing_stop_percent ?? 3]}
                      min={1}
                      max={15}
                      step={0.5}
                      onValueChange={([value]) => updateConfig('protection_params.trailing_stop_percent', value)}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Post-Sell Cooldown */}
            <Card className="matrix-card rounded-none border border-[#003B00]" data-testid="post-sell-cooldown-card">
              <CardHeader className="border-b border-[#003B00] pb-3">
                <CardTitle className="text-[#00FF41] text-sm tracking-widest flex items-center gap-2">
                  <Clock className="h-4 w-4 text-cyan-400" />
                  POST-SELL COOLDOWN
                </CardTitle>
                <CardDescription className="text-[#008F11] text-xs mt-1">
                  Prevents buying immediately after selling high. Per-coin cooldown.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-[#008F11] text-xs">ENABLE COOLDOWN</Label>
                  <Switch
                    data-testid="cooldown-enabled-switch"
                    checked={config?.protection_params?.sell_cooldown_enabled ?? true}
                    onCheckedChange={(checked) => updateConfig('protection_params.sell_cooldown_enabled', checked)}
                  />
                </div>
                {config?.protection_params?.sell_cooldown_enabled && (
                  <div className="space-y-2">
                    <Label className="text-[#008F11] text-xs">
                      COOLDOWN: {config?.protection_params?.sell_cooldown_minutes ?? 1} min
                    </Label>
                    <Slider
                      data-testid="cooldown-slider"
                      value={[config?.protection_params?.sell_cooldown_minutes ?? 1]}
                      min={0}
                      max={5}
                      step={0.5}
                      onValueChange={([value]) => updateConfig('protection_params.sell_cooldown_minutes', value)}
                      className="[&_[role=slider]]:bg-cyan-500"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* The Skim Settings */}
            <Card className="matrix-card rounded-none border border-[#003B00]">
              <CardHeader className="border-b border-[#003B00] pb-3">
                <CardTitle className="text-[#00FF41] text-sm tracking-widest flex items-center gap-2">
                  <Wallet className="h-4 w-4" />
                  THE SKIM (OPTIONAL)
                </CardTitle>
                <CardDescription className="text-[#008F11] text-xs mt-1">
                  Auto-transfer profits to your wallet
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-[#008F11] text-xs">ENABLE SKIM</Label>
                  <Switch
                    checked={config?.skim_params?.skim_enabled ?? false}
                    onCheckedChange={(checked) => updateConfig('skim_params.skim_enabled', checked)}
                  />
                </div>
                
                {config?.skim_params?.skim_enabled && (
                  <div className="space-y-4 pl-4 border-l border-[#003B00]">
                    <div className="space-y-2">
                      <Label className="text-[#008F11] text-xs">WALLET ADDRESS (BTC)</Label>
                      <Input
                        type="text"
                        placeholder="Your BTC wallet address"
                        value={config?.skim_params?.skim_wallet_address || ''}
                        onChange={(e) => updateConfig('skim_params.skim_wallet_address', e.target.value)}
                        className="matrix-input text-sm"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-[#008F11] text-xs flex items-center gap-2">
                        <DollarSign className="h-3 w-3" />
                        TRIGGER AMOUNT: ${config?.skim_params?.skim_trigger_amount ?? 500}
                      </Label>
                      <Slider
                        value={[config?.skim_params?.skim_trigger_amount ?? 500]}
                        min={100}
                        max={2000}
                        step={100}
                        onValueChange={([value]) => updateConfig('skim_params.skim_trigger_amount', value)}
                      />
                      <p className="text-[#003B00] text-xs">Transfer triggers every ${config?.skim_params?.skim_trigger_amount ?? 500} profit</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-[#008F11] text-xs flex items-center gap-2">
                        <DollarSign className="h-3 w-3" />
                        TRANSFER AMOUNT: ${config?.skim_params?.skim_transfer_amount ?? 5}
                      </Label>
                      <Slider
                        value={[config?.skim_params?.skim_transfer_amount ?? 5]}
                        min={1}
                        max={50}
                        step={1}
                        onValueChange={([value]) => updateConfig('skim_params.skim_transfer_amount', value)}
                      />
                      <p className="text-[#003B00] text-xs">Amount to transfer each time</p>
                    </div>
                  </div>
                )}
                
                {!config?.skim_params?.skim_enabled && (
                  <p className="text-[#003B00] text-xs">
                    When enabled, the bot will auto-transfer profits to your wallet after reaching the trigger amount
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Active Pairs */}
            <Card className="matrix-card rounded-none border border-[#003B00] lg:col-span-2">
              <CardHeader className="border-b border-[#003B00] pb-3">
                <CardTitle className="text-[#00FF41] text-sm tracking-widest">
                  ACTIVE TRADING PAIRS
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <CoinOverviewTable
                  selectable={true}
                  compact={true}
                  activePairs={config?.active_pairs || []}
                  onPairToggle={(pair) => {
                    const currentPairs = config?.active_pairs || [];
                    const newPairs = currentPairs.includes(pair)
                      ? currentPairs.filter(p => p !== pair)
                      : [...currentPairs, pair];
                    if (newPairs.length > 0) {
                      updateConfig('active_pairs', newPairs);
                    }
                  }}
                />
                <p className="text-xs text-[#008F11] mt-3">Click rows to toggle trading pairs on/off</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-4">
          <Card className="matrix-card rounded-none border border-[#003B00]">
            <CardHeader className="border-b border-[#003B00] pb-3">
              <CardTitle className="text-[#00FF41] text-sm tracking-widest flex items-center gap-2">
                <Bell className="h-4 w-4" />
                NOTIFICATION PREFERENCES
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-[#008F11] text-xs font-bold tracking-wider">EMAIL NOTIFICATIONS</h3>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">ENABLE EMAIL ALERTS</Label>
                    <Switch
                      checked={config?.notification_params?.email_alerts_enabled ?? false}
                      onCheckedChange={(checked) => updateConfig('notification_params.email_alerts_enabled', checked)}
                      data-testid="email-alerts-toggle"
                    />
                  </div>
                  
                  {config?.notification_params?.email_alerts_enabled && (
                    <div className="space-y-2 pl-4 border-l border-[#003B00]">
                      <Label className="text-[#008F11] text-xs">EMAIL ADDRESS</Label>
                      <Input
                        type="email"
                        value={config?.notification_params?.alert_email ?? user?.email ?? ''}
                        onChange={(e) => updateConfig('notification_params.alert_email', e.target.value)}
                        className="matrix-input rounded-none"
                        data-testid="email-address-input"
                      />
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-[#008F11] text-xs font-bold tracking-wider">ALERT TYPES</h3>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">TRADE ALERTS (EMAIL)</Label>
                    <Switch
                      checked={config?.notification_params?.alert_on_trade ?? true}
                      onCheckedChange={(checked) => updateConfig('notification_params.alert_on_trade', checked)}
                      data-testid="trade-alerts-toggle"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">BROWSER PUSH NOTIFICATIONS</Label>
                    <Switch
                      checked={config?.notification_params?.browser_push_enabled ?? false}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          try {
                            if ('Notification' in window && Notification.requestPermission) {
                              Notification.requestPermission().then(permission => {
                                updateConfig('notification_params.browser_push_enabled', true);
                                if (permission !== 'granted') {
                                  toast.info('Browser may block notifications — setting saved anyway');
                                }
                              }).catch(() => {
                                updateConfig('notification_params.browser_push_enabled', true);
                              });
                            } else {
                              updateConfig('notification_params.browser_push_enabled', true);
                              toast.info('Browser push not supported — setting saved for compatible browsers');
                            }
                          } catch {
                            updateConfig('notification_params.browser_push_enabled', true);
                          }
                        } else {
                          updateConfig('notification_params.browser_push_enabled', false);
                        }
                      }}
                      data-testid="browser-push-toggle"
                    />
                  </div>

                  <p className="text-[#003B00] text-[10px] tracking-wider mt-4 mb-1">EVENT FILTERS</p>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">NOTIFY ON BUYS</Label>
                    <Switch
                      checked={config?.notification_params?.notify_on_buy ?? true}
                      onCheckedChange={(checked) => updateConfig('notification_params.notify_on_buy', checked)}
                      data-testid="notify-buy-toggle"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">NOTIFY ON SELLS</Label>
                    <Switch
                      checked={config?.notification_params?.notify_on_sell ?? true}
                      onCheckedChange={(checked) => updateConfig('notification_params.notify_on_sell', checked)}
                      data-testid="notify-sell-toggle"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">NOTIFY ON STOP-LOSS / PROTECTION</Label>
                    <Switch
                      checked={config?.notification_params?.notify_on_protection ?? true}
                      onCheckedChange={(checked) => updateConfig('notification_params.notify_on_protection', checked)}
                      data-testid="notify-protection-toggle"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">ALERT WHEN BOT STOPS</Label>
                    <Switch
                      checked={config?.notification_params?.alert_on_bot_stop ?? true}
                      onCheckedChange={(checked) => updateConfig('notification_params.alert_on_bot_stop', checked)}
                      data-testid="notify-bot-stop-toggle"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">DAILY SUMMARY</Label>
                    <Switch
                      checked={config?.notification_params?.daily_summary_enabled ?? false}
                      onCheckedChange={(checked) => updateConfig('notification_params.daily_summary_enabled', checked)}
                      data-testid="daily-summary-toggle"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">WEEKLY DIGEST</Label>
                    <Switch
                      checked={config?.notification_params?.weekly_digest_enabled ?? true}
                      onCheckedChange={(checked) => updateConfig('notification_params.weekly_digest_enabled', checked)}
                      data-testid="weekly-digest-toggle"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">PROFIT MILESTONES</Label>
                    <Switch
                      checked={config?.notification_params?.profit_milestones ?? true}
                      onCheckedChange={(checked) => updateConfig('notification_params.profit_milestones', checked)}
                      data-testid="profit-milestones-toggle"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">AUDIO ALERTS</Label>
                    <Switch
                      checked={config?.notification_params?.audio_alerts ?? true}
                      onCheckedChange={(checked) => updateConfig('notification_params.audio_alerts', checked)}
                      data-testid="audio-alerts-toggle"
                    />
                  </div>

                  <p className="text-[#003B00] text-[10px] tracking-wider mt-4 mb-1">SMS NOTIFICATIONS</p>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">SMS ALERTS</Label>
                    <Switch
                      checked={config?.notification_params?.sms_enabled ?? false}
                      onCheckedChange={(checked) => updateConfig('notification_params.sms_enabled', checked)}
                      data-testid="sms-enabled-toggle"
                    />
                  </div>

                  {config?.notification_params?.sms_enabled && (
                    <>
                      <div>
                        <Label className="text-[#008F11] text-xs mb-1 block">PHONE NUMBER</Label>
                        <Input
                          value={config?.notification_params?.phone_number || ''}
                          onChange={(e) => updateConfig('notification_params.phone_number', e.target.value)}
                          placeholder="+1234567890"
                          className="bg-black border-[#003B00] text-[#00FF41] font-mono text-xs"
                          data-testid="sms-phone-input"
                        />
                      </div>
                      <div>
                        <Label className="text-[#008F11] text-xs mb-1 block">CARRIER</Label>
                        <select
                          value={config?.notification_params?.sms_carrier || ''}
                          onChange={(e) => updateConfig('notification_params.sms_carrier', e.target.value)}
                          className="w-full bg-black border border-[#003B00] text-[#00FF41] font-mono text-xs p-2 rounded"
                          data-testid="sms-carrier-select"
                        >
                          <option value="">Select carrier</option>
                          <option value="att">AT&T</option>
                          <option value="verizon">Verizon</option>
                          <option value="tmobile">T-Mobile</option>
                          <option value="sprint">Sprint</option>
                        </select>
                      </div>
                    </>
                  )}

                  <p className="text-[#003B00] text-[10px] tracking-wider mt-4 mb-1">TELEGRAM NOTIFICATIONS</p>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs flex items-center gap-1.5"><MessageSquare className="w-3 h-3" /> ENABLE TELEGRAM</Label>
                    <Switch
                      checked={config?.notification_params?.telegram_enabled ?? false}
                      onCheckedChange={(checked) => updateConfig('notification_params.telegram_enabled', checked)}
                      data-testid="telegram-enabled-toggle"
                    />
                  </div>

                  {config?.notification_params?.telegram_enabled && (
                    <div className="space-y-3 pl-4 border-l border-[#003B00]">
                      <div>
                        <Label className="text-[#008F11] text-xs mb-1 block">BOT TOKEN</Label>
                        <Input
                          value={config?.notification_params?.telegram_bot_token || ''}
                          onChange={(e) => updateConfig('notification_params.telegram_bot_token', e.target.value)}
                          placeholder="123456:ABC-DEF..."
                          className="bg-black border-[#003B00] text-[#00FF41] font-mono text-xs"
                          data-testid="telegram-bot-token-input"
                        />
                        <p className="text-[#003B00] text-[9px] mt-1">Get from @BotFather on Telegram</p>
                      </div>
                      <div>
                        <Label className="text-[#008F11] text-xs mb-1 block">CHAT ID</Label>
                        <Input
                          value={config?.notification_params?.telegram_chat_id || ''}
                          onChange={(e) => updateConfig('notification_params.telegram_chat_id', e.target.value)}
                          placeholder="123456789"
                          className="bg-black border-[#003B00] text-[#00FF41] font-mono text-xs"
                          data-testid="telegram-chat-id-input"
                        />
                        <p className="text-[#003B00] text-[9px] mt-1">Get from @userinfobot on Telegram</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-cyan-600 text-cyan-400 hover:bg-cyan-900/30 text-xs w-full"
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API_V2}/user/test-telegram`, {}, { withCredentials: true });
                            if (res.data?.success) toast.success('Telegram test message sent!');
                            else toast.error('Telegram test failed');
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Telegram test failed');
                          }
                        }}
                        data-testid="telegram-test-btn"
                      >
                        <Send className="w-3 h-3 mr-1" /> SEND TEST MESSAGE
                      </Button>

                      <p className="text-[#003B00] text-[10px] tracking-wider mt-3 mb-1">TELEGRAM EVENT FILTERS</p>

                      <div className="flex items-center justify-between">
                        <Label className="text-[#008F11] text-xs">TRADE ALERTS</Label>
                        <Switch
                          checked={config?.notification_params?.telegram_on_trade ?? true}
                          onCheckedChange={(checked) => updateConfig('notification_params.telegram_on_trade', checked)}
                          data-testid="telegram-trade-toggle"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-[#008F11] text-xs">BOT STOP ALERTS</Label>
                        <Switch
                          checked={config?.notification_params?.telegram_on_bot_stop ?? true}
                          onCheckedChange={(checked) => updateConfig('notification_params.telegram_on_bot_stop', checked)}
                          data-testid="telegram-bot-stop-toggle"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-[#008F11] text-xs">DAILY P/L SUMMARY</Label>
                        <Switch
                          checked={config?.notification_params?.telegram_daily_summary ?? false}
                          onCheckedChange={(checked) => updateConfig('notification_params.telegram_daily_summary', checked)}
                          data-testid="telegram-daily-toggle"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-[#008F11] text-xs">NEW BADGE EARNED</Label>
                        <Switch
                          checked={config?.notification_params?.telegram_on_badge ?? true}
                          onCheckedChange={(checked) => updateConfig('notification_params.telegram_on_badge', checked)}
                          data-testid="telegram-badge-toggle"
                        />
                      </div>
                    </div>
                  )}

                  <p className="text-[#003B00] text-[10px] tracking-wider mt-4 mb-1">BADGE NOTIFICATIONS</p>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#008F11] text-xs">EMAIL ON NEW BADGE</Label>
                    <Switch
                      checked={config?.notification_params?.notify_on_badge ?? true}
                      onCheckedChange={(checked) => updateConfig('notification_params.notify_on_badge', checked)}
                      data-testid="notify-badge-toggle"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
};

export default UserConfigPage;
